import { z } from 'zod';
/**
 * Data de calendário no formato ISO (`2026-10-19`), sem hora nem fuso. Datas
 * do arquivo são dias, não instantes: tratá-las como texto evita que um fuso
 * mude o dia.
 */
export const isoDateSchema = z.iso.date();
/** Prazo de guarda: 5 anos e 6 meses a partir da data final. Escopo, 1.4. */
export const RETENTION_MONTHS = 5 * 12 + 6;
const MONTHS_PER_YEAR = 12;
const pad = (value, length) => String(value).padStart(length, '0');
/** Último dia do mês (mês de 1 a 12). */
const lastDayOf = (year, month) => new Date(Date.UTC(year, month, 0)).getUTCDate();
/**
 * Soma meses a uma data ISO. Quando o dia não existe no mês de destino, vale
 * o último dia dele: 31/08 + 6 meses = 28/02 (ou 29/02).
 */
export function addMonths(isoDate, months) {
    const [year = 0, month = 1, day = 1] = isoDate.split('-').map(Number);
    const monthIndex = month - 1 + months;
    const targetYear = year + Math.floor(monthIndex / MONTHS_PER_YEAR);
    const targetMonth = (monthIndex % MONTHS_PER_YEAR) + 1;
    const targetDay = Math.min(day, lastDayOf(targetYear, targetMonth));
    return `${pad(targetYear, 4)}-${pad(targetMonth, 2)}-${pad(targetDay, 2)}`;
}
/** Data a partir da qual a caixa pode ser descartada (regra R7). */
export const discardDateOf = (periodEnd) => addMonths(periodEnd, RETENTION_MONTHS);
/**
 * Status da caixa. Gravado, `ok` quer dizer "segue a data": a caixa passa a
 * `obsolete` sozinha na data de expurgo (R8). `obsolete` gravado é a
 * marcação manual, antes da data (R8a); `cancelled`, o cancelamento de um
 * cadastro errado (R5a); `discarded`, o registro de que a caixa obsoleta foi
 * descartada (R10). Sem tabela de status: cada um tem comportamento no
 * código, então um status novo é sempre código + migration (a constraint
 * `CK_boxes_status`).
 */
export const boxStatusSchema = z.enum([
    'ok',
    'obsolete',
    'cancelled',
    'discarded',
]);
/**
 * Status que a rota de mudança de status aceita. O descarte tem rota e
 * permissão próprias (R10).
 */
export const statusChangeTargetSchema = boxStatusSchema.exclude(['discarded']);
/** Como a pessoa vê cada status, na tela e no histórico. */
export const BOX_STATUS_LABELS = {
    ok: 'OK',
    obsolete: 'Obsoleta',
    cancelled: 'Cancelada',
    discarded: 'Descartada',
};
/**
 * Cancelada e descartada são finais: a caixa não aceita mais alteração nem
 * etiqueta, e fica só para consulta e histórico.
 */
export const isClosedStatus = (status) => status === 'cancelled' || status === 'discarded';
/**
 * Só a caixa obsoleta (a que a pessoa vê assim, pela data ou marcada) pode
 * ser descartada: antes disso, o prazo de guarda ainda vale (R10).
 */
export const canDiscard = (shown) => shown === 'obsolete';
/**
 * Status que a pessoa vê: o gravado, ou `obsolete` quando a data de expurgo
 * chegou (inclusive no próprio dia). Datas ISO de mesmo formato se comparam
 * como texto.
 */
export const boxStatusOf = (stored, discardDate, today) => (stored === 'ok' && today >= discardDate ? 'obsolete' : stored);
//# sourceMappingURL=retention.js.map